// The RC's MQTT session: Pilot 2's thing module, brought up without tearing
// down one that is live.
//
// Ported from the Flutter page's `ensureMqttConnected` (load_modules.dart,
// release 20261021.01), which the RC confirmed on 2026-10-21: a fresh Pilot
// page has no thing module (615011 "thing module not load") and connected
// only after the second thingConnect, 5 s in; a reload found it loaded and
// connected and kept the session. Reloading the module over a live session
// makes Pilot close it (EMQX {shutdown,normal} ~2 s after the page start),
// which is what cut the RC on every reload until then (autrik_codex T90 §4.1).
// The trace lines and outcomes are the Flutter page's, word for word, so
// bugsink groups the two pages' reports together (T90 §4.2).

import type { Bridge } from './bridge';

export interface ThingTarget {
  host: string; // e.g. ssl://autrikcloud.com:8884
  deviceSn: string; // the RC's serial: EMQX username and client id
  token: string; // EMQX JWT, the password
}

export interface ThingDeps {
  bridge: Bridge;
  sleep: (ms: number) => Promise<void>;
  report: (outcome: string, deviceSn: string, trace: string[]) => void;
}

/** Whether the session is up, by thingGetConnectState alone (never isComponentLoaded). */
export function isThingConnected(bridge: Bridge): boolean {
  try {
    const r = bridge.reply('thingGetConnectState');
    return r.code === 0 && r.data === true;
  } catch {
    return false;
  }
}

/** Pilot 2's raw answers about the thing module, for the start report. */
export function thingBridgeState(bridge: Bridge): string {
  try {
    return (
      `isComponentLoaded=${bridge.raw('platformIsComponentLoaded', 'thing')} ` +
      `thingGetConnectState=${bridge.raw('thingGetConnectState')}`
    );
  } catch (e) {
    return `bridge error: ${String(e)}`;
  }
}

/**
 * Load the thing module and ask Pilot to connect. The callback is named
 * `connectCallback` on every call: the Flutter page's djisdk.js passed that
 * name whatever it was given, so this is what Pilot has always seen; main.ts
 * defines `window.connectCallback`.
 */
export function loadThing(bridge: Bridge, t: ThingTarget): void {
  bridge.loadComponent('thing', {
    host: t.host,
    connectCallback: 'connectCallback',
    username: t.deviceSn,
    password: t.token,
  });
  bridge.thingGetConfigs();
  bridge.thingConnect(t.deviceSn, t.token, 'connectCallback');
}

/** Load, and say how it went for the trace: `loaded`, or Pilot's refusal. */
function tryLoad(bridge: Bridge, t: ThingTarget): string {
  try {
    loadThing(bridge, t);
    return 'loaded';
  } catch (e) {
    return `load failed: ${e instanceof Error ? e.message : String(e)}`;
  }
}

/**
 * Skip the load when the session is up, otherwise load it; then watch it for
 * 15 s: one that drops is loaded again, one loaded that has not connected 5 s
 * later is asked once more. One report per start. [atPageStart] is
 * thingBridgeState before any module was loaded. Resolves with the outcome.
 */
export async function ensureThingConnected(
  deps: ThingDeps,
  t: ThingTarget,
  atPageStart: string,
): Promise<string> {
  const { bridge } = deps;
  const trace = [`page start: ${atPageStart}`, `decision: ${thingBridgeState(bridge)}`];
  const connected = isThingConnected(bridge);
  if (connected) {
    trace.push('left as it is');
  } else {
    trace.push(tryLoad(bridge, t));
  }
  const leftAlone = connected;
  let loadedAt = connected ? -1 : 0;
  const done = (outcome: string): string => {
    deps.report(outcome, t.deviceSn, trace);
    return outcome;
  };

  for (let second = 1; second <= 15; second++) {
    await deps.sleep(1000);
    const up = isThingConnected(bridge);
    if (loadedAt < 0) {
      if (up) continue;
      trace.push(`${second}s: dropped: ${thingBridgeState(bridge)}`, `${second}s: ${tryLoad(bridge, t)}`);
      loadedAt = second;
      continue;
    }
    if (up) {
      trace.push(`${second}s: connected`);
      return done(leftAlone ? 'dropped after start, reconnected' : 'loaded, connected');
    }
    if (second - loadedAt === 5) {
      bridge.thingConnect(t.deviceSn, t.token, 'connectCallback');
      trace.push(`${second}s: thingConnect again`);
    }
  }
  if (loadedAt < 0) return done('left as it is, stayed up');
  trace.push(`15s: ${thingBridgeState(bridge)}`);
  return done('not connected after 15s');
}
